import { Constants, isUkcAgilityDynamicClass } from "@eagerdog/constants";

import {
  ScoringStrategy,
  ScoringContext,
  ValidationResult,
  ValidationError,
  ok,
  fail,
} from "./types";
import {
  hasValidSearchTime,
  hasValidCourseTime,
  hasResultType,
} from "./validation";

const C = Constants;

/**
 * The scoring registry: the single source of truth mapping each show type to its
 * API endpoint suffix and its result-entry validation. Consumed by both the web
 * Results tab and the mobile Scoring screens.
 */
export const SCORING_STRATEGIES: Record<string, ScoringStrategy> = {
  [C.show_type.nosework]: { showType: C.show_type.nosework, apiPath: "nosework" },
  [C.show_type.fast_cat]: { showType: C.show_type.fast_cat, apiPath: "fastcat" },
  [C.show_type.fast_dash]: {
    showType: C.show_type.fast_dash,
    apiPath: "fastdash",
  },
  [C.show_type.cat]: { showType: C.show_type.cat, apiPath: "cat" },
  [C.show_type.fetch]: {
    showType: C.show_type.fetch,
    apiPath: "fetch",
    requiresJudgeApproval: true,
    validate: (results): ValidationResult => {
      if (!results?.approved_by_judge) {
        return fail("Fetch results must be approved by a judge");
      }
      const errors = judgeCredErrors(C.show_type.fetch, results);
      return errors.length ? { valid: false, errors } : ok();
    },
  },
  [C.show_type.country_rat_race]: {
    showType: C.show_type.country_rat_race,
    apiPath: "country-rat-race",
  },
  [C.show_type.urban_rat_race]: {
    showType: C.show_type.urban_rat_race,
    apiPath: "urban-rat-race",
  },
  [C.show_type.lost_and_found]: {
    showType: C.show_type.lost_and_found,
    apiPath: "lost-and-found",
  },
  [C.show_type.obedience]: {
    showType: C.show_type.obedience,
    apiPath: "obedience",
  },
  [C.show_type.rally_obedience]: {
    showType: C.show_type.rally_obedience,
    apiPath: "rally-obedience",
  },
  [C.show_type.scentwork]: {
    showType: C.show_type.scentwork,
    apiPath: "scentwork",
    isMultiResult: true,
  },
  [C.show_type.dock_jumping]: {
    showType: C.show_type.dock_jumping,
    apiPath: "dock-jumping",
  },
  [C.show_type.shed_dog]: {
    showType: C.show_type.shed_dog,
    apiPath: "shed-dog",
  },
  [C.show_type.precision_coursing]: {
    showType: C.show_type.precision_coursing,
    apiPath: "precision-coursing",
  },
  [C.show_type.weight_pull]: {
    showType: C.show_type.weight_pull,
    apiPath: "weight-pull",
    validate: (results): ValidationResult => {
      const allowed = [
        C.score_result_type.qualified,
        C.score_result_type.not_qualified,
        C.score_result_type.absent,
      ];
      if (!results?.result_type || !allowed.includes(results.result_type)) {
        return fail("Please enter a result", "result_type");
      }
      if (results.result_type !== C.score_result_type.qualified) {
        return ok();
      }
      if (!results.weight_class || results.weight_class === "None") {
        return fail("Please select a Weight Class", "weight_class");
      }
      if (!results.highest_weight || Number(results.highest_weight) <= 0) {
        return fail("Please enter the highest weight pulled", "highest_weight");
      }
      if (!results.time || Number(results.time) <= 0 || isNaN(Number(results.time))) {
        return fail("Please enter the time", "time");
      }
      return ok();
    },
  },
  [C.show_type.farm_dog]: {
    showType: C.show_type.farm_dog,
    apiPath: "farm-dog",
    requiresJudgeApproval: true,
    validate: (results): ValidationResult => {
      if (!results?.approved_by_judge) {
        return fail("Farm Dog results must be approved by a judge");
      }
      const errors = judgeCredErrors(C.show_type.farm_dog, results);
      return errors.length ? { valid: false, errors } : ok();
    },
  },
  [C.show_type.water_dog]: {
    showType: C.show_type.water_dog,
    apiPath: "water-dog",
  },
  [C.show_type.agility]: {
    showType: C.show_type.agility,
    apiPath: (ctx: ScoringContext) =>
      ctx.sanctioningClub === C.sanctioning_club.AKC
        ? "akc-agility"
        : "agility",
    validate: (results, ctx): ValidationResult => {
      // Validation only applies to UKC agility (per web EnterResults).
      if (ctx.sanctioningClub !== C.sanctioning_club.UKC) {
        return ok();
      }
      if (results?.result_type !== C.score_result_type.qualified) {
        return ok();
      }
      const isDynamicClass =
        ctx.dogClass?.level && isUkcAgilityDynamicClass(ctx.dogClass.level);
      if (!results?.time) {
        return fail("Agility qualified results must include run time", "time");
      }
      if (!isDynamicClass && !results?.score) {
        return fail("Agility qualified results must include score", "score");
      }
      return ok();
    },
  },
  [C.show_type.herding]: {
    showType: C.show_type.herding,
    apiPath: "herding",
    validate: (results): ValidationResult => validateHerding(results),
  },
  [C.show_type.pre_trial_herding]: {
    showType: C.show_type.pre_trial_herding,
    apiPath: "herding",
  },
  [C.show_type.instinct_test_herding]: {
    showType: C.show_type.instinct_test_herding,
    apiPath: "herding",
  },
  [C.show_type.scent]: {
    showType: C.show_type.scent,
    apiPath: "scent",
    validate: (results): ValidationResult => {
      const validResultTypes: string[] = [
        C.score_result_type.qualified,
        C.score_result_type.not_qualified,
        C.cwags_scent_result.pass,
        C.cwags_scent_result.fail,
        C.cwags_scent_result.abs,
      ];
      if (!validResultTypes.includes(results?.result_type)) {
        return fail("Please select Scent result type first", "result_type");
      }
      return ok();
    },
  },
  [C.show_type.barn_hunt]: {
    showType: C.show_type.barn_hunt,
    apiPath: "barn-hunt",
    validate: (results): ValidationResult => {
      if (!results || !results.result_type) {
        return fail("Please select a result type first", "result_type");
      }
      return ok();
    },
  },
  [C.show_type.scent_dog_games]: {
    showType: C.show_type.scent_dog_games,
    apiPath: "scent-dog-games",
  },
};

const judgeCredErrors = (showType: string, results: any): ValidationError[] => {
  const errors: ValidationError[] = [];
  if (!results?.judge_name) {
    errors.push({
      field: "judge_name",
      message: `${showType} results must include a judge's name`,
    });
  }
  if (!results?.judge_number) {
    errors.push({
      field: "judge_number",
      message: `${showType} results must include a judge's number`,
    });
  }
  return errors;
};

const validateHerding = (results: any): ValidationResult => {
  // Only the full herding show type carries this rule (pre-trial / instinct skip it).
  if (
    [
      C.score_result_type.qualified,
      C.score_result_type.not_qualified,
    ].includes(results?.result_type) &&
    (results?.elapsed_time <= 0 || results?.total_points_deducted < 0)
  ) {
    return fail(
      "Herding qualified results must include valid elapsed time and deduction points"
    );
  }
  return ok();
};

export const getStrategy = (
  showType: string
): ScoringStrategy | undefined => SCORING_STRATEGIES[showType];

/** Whether mobile/web scoring supports entering results for this show type. */
export const isScoringSupported = (showType: string): boolean =>
  !!SCORING_STRATEGIES[showType];

/** Resolve the PUT endpoint suffix for a show type given context. */
export const resolveApiPath = (
  showType: string,
  ctx: ScoringContext = {}
): string | undefined => {
  const strategy = SCORING_STRATEGIES[showType];
  if (!strategy) return undefined;
  return typeof strategy.apiPath === "function"
    ? strategy.apiPath(ctx)
    : strategy.apiPath;
};

/**
 * Run the complete validation pipeline for a score entry:
 *  1. generic search/course time format
 *  2. generic result-type presence
 *  3. show-type-specific rules
 *
 * Scratch / conflict selections are handled by the caller and bypass this.
 */
export const validateScore = (
  showType: string,
  results: any,
  ctx: ScoringContext = {},
  scentworkResults: any[] = []
): ValidationResult => {
  const errors: ValidationError[] = [];

  const validSearch = hasValidSearchTime(showType, results, scentworkResults);
  const validCourse = hasValidCourseTime(showType, results, scentworkResults);

  if (!validSearch) {
    errors.push({
      field: "search_time",
      message:
        "Please enter the search time in the correct format (two digits for each input)",
    });
  }
  if (!validCourse) {
    errors.push({
      field: "course_time",
      message:
        "Please enter the course time in the correct format (two digits for each input)",
    });
  }

  if (!hasResultType(showType, results, scentworkResults)) {
    errors.push({ field: "result_type", message: "Please enter a result" });
  }

  // Short-circuit on generic failures, mirroring web ordering.
  if (errors.length) {
    return { valid: false, errors };
  }

  const strategy = SCORING_STRATEGIES[showType];
  if (strategy?.validate) {
    const target = strategy.isMultiResult ? scentworkResults : results;
    const specific = strategy.validate(target, ctx);
    if (!specific.valid) {
      return specific;
    }
  }

  return ok();
};
